import { useCallback } from 'react'

import { type QueryKey, useQuery } from '@tanstack/react-query'
import { identity } from 'es-toolkit'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import {
  type LibrarySortDirection,
  type LibrarySortOption,
  libraryEntitiesQueryKeys,
} from '@/hooks/library/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { Entity, EntityType } from '@/types/library/Entity'

interface UseEntitiesArgs<T> {
  orgId: UUID | ''
  entityTypes?: EntityType[]
  limit?: number
  offset?: number
  sort?: LibrarySortOption
  order?: LibrarySortDirection
  q?: string
  enabled?: boolean
  withConfirmed?: boolean
  withSuggested?: boolean
  select?: (data: PaginatedResponse<Entity>) => T
}

export const useEntities = <T = Entity[]>({
  orgId,
  entityTypes,
  limit,
  offset,
  sort,
  order,
  q,
  enabled = true,
  withConfirmed,
  withSuggested,
  select,
}: UseEntitiesArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: PaginatedResponse<Entity>) => {
      if (select) {
        return select(data)
      }
      return data.data as unknown as T
    },
    [select],
  )

  const pagination: PaginateRequest | undefined =
    limit !== undefined ? { limit, offset: offset ?? 0 } : undefined

  const queryKey = libraryEntitiesQueryKeys.organization({
    orgId,
    ...(entityTypes ? { entityTypes } : {}),
    ...(pagination ? { limit, offset: offset ?? 0 } : {}),
    ...(sort ? { sort } : {}),
    ...(order ? { order } : {}),
    ...(q ? { q } : {}),
    withConfirmed,
    withSuggested,
  })

  return useQuery({
    queryKey,
    queryFn: () =>
      fetchEntities(
        orgId,
        accessToken,
        entityTypes,
        pagination,
        sort,
        order,
        q,
        withConfirmed,
        withSuggested,
      ),
    enabled: !!orgId && !!accessToken && enabled,
    staleTime: Infinity,
    placeholderData: identity,
    select: selectFn,
  })
}

export const isQueryKeyPrefix = (queryKey: QueryKey, orgId: UUID | '') => {
  if (!orgId) {
    return false
  }
  return queryKey[0] === 'library_entities' && queryKey[1] === orgId
}

export const fetchEntities = async (
  orgId: UUID | '',
  accessToken: string,
  entityTypes?: EntityType[],
  pagination?: PaginateRequest,
  sort?: LibrarySortOption,
  order?: LibrarySortDirection,
  q?: string,
  withConfirmed?: boolean,
  withSuggested?: boolean,
): Promise<PaginatedResponse<Entity>> => {
  if (!orgId) {
    throw new Error('orgId is required')
  }

  const url = new URL(`${API_URL}/api/v1/organizations/${orgId}/entities`)

  if (entityTypes && entityTypes.length > 0) {
    entityTypes.forEach((t) => url.searchParams.append('entity_types', t))
  }
  if (sort) {
    url.searchParams.append('sort', sort)
  }
  if (order) {
    url.searchParams.append('order', order)
  }
  if (q) {
    url.searchParams.append('q', q)
  }
  if (typeof withConfirmed === 'boolean') {
    url.searchParams.append('with_confirmed', String(withConfirmed))
  }
  if (typeof withSuggested === 'boolean') {
    url.searchParams.append('with_suggested', String(withSuggested))
  }
  if (pagination) {
    if (pagination.limit) {
      url.searchParams.append('limit', String(pagination.limit))
    }
    if (pagination.offset !== undefined) {
      url.searchParams.append('offset', String(pagination.offset))
    }
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })
  if (!response.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: response.statusText,
      cause: response,
    })
  }
  return (await response.json()) as PaginatedResponse<Entity>
}
